import { useEffect, useState } from 'react'

/**
 * Theme management: read/write localStorage and toggle the light class on documentElement.
 * Dark is the default; index.html applies the stored theme before first paint.
 * This module keeps it consistent.
 */

export type Theme = 'light' | 'dark' | 'system'

const STORAGE_KEY = 'theme'

/** Read the persisted theme from localStorage, or return 'system' if not set. */
export function getStoredTheme(): Theme {
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored === 'light' || stored === 'dark') return stored
  return 'system'
}

/** Apply a theme: toggle the light class and update localStorage. */
export function applyTheme(theme: Theme): void {
  const light = theme === 'light' || (theme === 'system' && matchMedia('(prefers-color-scheme: light)').matches)

  if (light) {
    document.documentElement.classList.add('light')
  } else {
    document.documentElement.classList.remove('light')
  }

  // 'system' means use OS preference; don't store anything so system default is used.
  if (theme === 'system') {
    localStorage.removeItem(STORAGE_KEY)
  } else {
    localStorage.setItem(STORAGE_KEY, theme)
  }
}

/** Hook to read and set the current theme. */
export function useTheme(): {
  theme: Theme
  setTheme: (t: Theme) => void
  isDark: boolean
} {
  const [theme, setThemeState] = useState<Theme>(() => getStoredTheme())
  const [isDark, setIsDark] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored ? stored === 'dark' : !matchMedia('(prefers-color-scheme: light)').matches
  })

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme)
    applyTheme(newTheme)

    // Update isDark to reflect the new theme.
    const dark = newTheme === 'dark' || (newTheme === 'system' && !matchMedia('(prefers-color-scheme: light)').matches)
    setIsDark(dark)
  }

  // Listen to OS preference changes when in system mode.
  useEffect(() => {
    if (theme !== 'system') return

    const media = matchMedia('(prefers-color-scheme: light)')
    const handler = () => {
      const dark = !media.matches
      setIsDark(dark)
      applyTheme('system')
    }
    media.addEventListener('change', handler)
    return () => media.removeEventListener('change', handler)
  }, [theme])

  return { theme, setTheme, isDark }
}
